import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	OnInit,
	Output,
} from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
	ZkEmployerTaxIdService,
	ZkIndividualTaxIdService,
	ZkNotificationService,
} from '@zenklub/ng-ui';
import { CacheService } from 'app/services/cache.service';
import * as moment from 'moment-timezone';

import { ApiError } from '../../interfaces/error.interface';
import {
	EBillingType,
	EProfileBillingStatus,
	IProfessionalFinancial,
	IProfessionalLegalPerson,
	IProfessionalNaturalPerson,
	IProfessionalPaymentAccount,
	IProfessionalPerson,
	IProfessionalPersonData,
	IProfessionalProfile,
} from '../../interfaces/professionalprofile.interface';
import { GlobalService } from '../../services/global.service';
import { UserService } from '../../services/user.service';

enum ECancellationDialogTypes {
	Cancel = 'cancel',
	RollbackToLegal = 'rollback-to-legal',
	RollbackToNatural = 'rollback-to-natural',
	RollbackLegalToBlank = 'rollback-legal-to-blank',
	RollbackNaturalToBlank = 'rollback-natural-to-blank',
}

@Component({
	selector: 'app-my-account-financial',
	templateUrl: './financial.component.html',
	styleUrls: ['./financial.component.scss'],
	providers: [ZkIndividualTaxIdService, ZkEmployerTaxIdService],
})
export class MyAccountFinancialComponent implements OnInit {
	@Output() userUpdated = new EventEmitter();
	@Output() unsavedChanges = new EventEmitter<boolean>();
	@Output() cpnjValidationTrigger = new EventEmitter<string>();
	isSaving: boolean;
	showAlertLabel = false;
	allowPaymentUpdate = false;
	hasPaymentAccount: boolean;
	timeRange: unknown;
	user: IProfessionalProfile;
	professionalPerson: IProfessionalPerson;
	billingStatus = EProfileBillingStatus;
	mobileMenu: string[] = ['DATA', 'ADDRESS', 'DOCUMENTS'];
	subPageName: string;
	billingTypes = EBillingType;
	billingTypeSelected: EBillingType;
	waitingIdwallResponse: boolean;

	updateConfirmationDialog: {
		isOpen: boolean;
		type: 'mobile' | 'desktop';
	};
	professionalFinancial: IProfessionalFinancial[];
	cancellationDialog: {
		isOpen: boolean;
		type: ECancellationDialogTypes;
		showDescription: boolean;
		isLoading?: boolean;
	};
	changeBillingTypeDialog: {
		type: EBillingType;
		isOpen: boolean;
		isLoading: boolean;
		nextChangeDate?: string;
	};
	cancellationDialogTypes = ECancellationDialogTypes;
	showRequiredFiles = false;
	forms: {
		naturalFormData: FormGroup;
		naturalFormAddress: FormGroup;
		legalFormData: FormGroup;
		legalFormAddress: FormGroup;
	};
	bankDataFormParams: FormGroup;
	documentsChanged: boolean;
	constructor(
		public activatedRoute: ActivatedRoute,
		private cpfService: ZkIndividualTaxIdService,
		private cnpjService: ZkEmployerTaxIdService,
		public changeDetector: ChangeDetectorRef,
		public globalService: GlobalService,
		public userService: UserService,
		private translateService: TranslateService,
		public notificationService: ZkNotificationService,
		private cacheService: CacheService
	) {}

	async ngOnInit() {
		this.initDialogStatus();
		await this.getUser();
		await this.setProfessionalPerson();
		this.initForms();

		if (
			this.isMobileDevice() &&
			this.activatedRoute.snapshot.fragment === 'payment-content'
		) {
			this.subPageName = 'BANK_DATA';
		}
		this.setPaymentMethodUpdateResponse(this.professionalPerson);
	}

	getValidationResponse() {
		const idwallResponse = this.cacheService.getStore('idwallResponse');
		this.professionalFinancial = idwallResponse?.data;

		if (this.professionalFinancial && this.professionalFinancial?.length >= 1) {
			if (this.professionalFinancial[0].status === 'APROVADO') {
				this.setTimeRange(this.professionalPerson);
				this.showAlertLabel = true;
			}
			if (this.professionalFinancial[0].status === 'REJEITADO') {
				this.showAlertLabel = false;
			}
		}
	}

	async setPaymentMethodUpdateResponse(professional) {
		const { ChangeAccepted, WaitingChange } = EProfileBillingStatus;
		this.setTimeRange(professional);

		if (
			[ChangeAccepted, WaitingChange].includes(professional.status) &&
			!professional?.canUpdatedPaymentAccount &&
			professional?.idWallReportStatus === 'APROVADO'
		) {
			this.showAlertLabel = true;
			const result = await this.getProfessionalPerson();
			this.professionalPerson.blockEditPaymentAccount =
				result.blockEditPaymentAccount;
		} else {
			this.showAlertLabel = false;
		}
	}

	setTimeRange(professional) {
		this.timeRange = {
			paymentAccountUpdateAt: professional.lastProfessionalPersonEndsAt,
		};
		return this.timeRange;
	}

	naturalPersonCardEnabled() {
		return this.professionalPerson?.type === this.billingTypes.Natural;
	}

	setRequiredProfileFields() {
		const naturalPerson = this.user.naturalPerson;
		const legalPerson = this.user.legalPerson;
		this.user.legalPerson = legalPerson || ({} as IProfessionalLegalPerson);
		this.user.naturalPerson =
			naturalPerson || ({} as IProfessionalNaturalPerson);

		this.user.naturalPerson.documents = naturalPerson?.documents || [];
		this.user.legalPerson.documents = legalPerson?.documents || [];
	}

	isMobileDevice(): boolean {
		return this.globalService.isMobileDevice();
	}

	selectNaturalBillingCard() {
		if (
			this.professionalPerson.status == this.billingStatus.Active &&
			!this.businessTypeIs(EBillingType.Natural)
		) {
			this.openChangeBillingTypeDialog(EBillingType.Natural);
			return;
		}
		if (!this.canSelectBillingCard(EBillingType.Natural)) {
			return;
		}

		this.billingTypeSelected = EBillingType.Natural;
		if (!this.canChangeBillingTypeAfterSelect(EBillingType.Natural)) {
			this.changeBillingTypeToPersonal();
		}
		this.enableCurrentFormFields();
	}

	businessTypeIs(billingType: EBillingType): boolean {
		return this.professionalPerson?.type == billingType;
	}

	selectLegalEntityBillingCard() {
		if (!this.canSelectBillingCard(EBillingType.Legal)) {
			return;
		}

		if (
			this.professionalPerson.type === EBillingType.Natural &&
			this.professionalPerson.status === this.billingStatus.Active
		) {
			this.clearStates();
		}

		this.billingTypeSelected = EBillingType.Legal;
		if (!this.canChangeBillingTypeAfterSelect(EBillingType.Legal)) {
			this.changeBillingTypeToLegalEntity();
		}
		this.enableCurrentFormFields();
	}

	clearStates() {
		this.forms.legalFormData = new FormGroup({
			fiscalId: new FormControl('', {
				validators: [
					Validators.required,
					this.validateControl(this.validateFiscalId),
				],
			}),
			businessFiscalId: new FormControl('', {
				validators: [
					Validators.required,
					this.validateControl(this.validateBusinessFiscalId),
				],
			}),
		});

		this.cacheService.deleteStore('idwallResponse');
	}

	canChangeBillingTypeAfterSelect(currentType: EBillingType) {
		const { status, type } = this.professionalPerson;
		const { WaitingApproval, InProgress } = this.billingStatus;

		return (
			[WaitingApproval, InProgress].includes(status) || type == currentType
		);
	}

	canSelectBillingCard(type: EBillingType) {
		const { status } = this.professionalPerson;
		const { ChangeAccepted, WaitingApproval, WaitingChange, ChangeRequested } =
			this.billingStatus;
		if (this.businessTypeIs(type)) {
			return true;
		}
		if (
			[
				ChangeAccepted,
				WaitingChange,
				ChangeRequested,
				WaitingApproval,
			].includes(status)
		) {
			return false;
		}
		return true;
	}

	enableCurrentFormFields() {
		if (this.forms) {
			if (this.professionalPerson?.type === this.billingTypes.Natural) {
				this.forms.naturalFormAddress?.enable();
				this.forms.naturalFormData?.enable();
				this.forms.legalFormData?.disable();
				this.forms.legalFormAddress?.disable();
			} else {
				this.forms.naturalFormAddress?.disable();
				this.forms.naturalFormData?.disable();
				this.forms.legalFormData?.enable();
			}
		}
	}

	rollbackBillingTypeToPersonal() {
		if (this.isNewProfileBilling()) {
			this.setProfileBillingTypeBlank();
			return;
		}
		this.professionalPerson.type = this.billingTypes.Natural;
		this.professionalPerson.status = EProfileBillingStatus.Active;
		this.selectNaturalBillingCard();
	}

	rollbackBillingTypeToLegalEntity() {
		if (this.isNewProfileBilling()) {
			this.setProfileBillingTypeBlank();
			return;
		}
		this.professionalPerson.type = this.billingTypes.Legal;
		this.professionalPerson.status = EProfileBillingStatus.Active;
		this.selectLegalEntityBillingCard();
	}

	setProfileBillingTypeBlank() {
		this.professionalPerson.type = null;
		this.professionalPerson.status = EProfileBillingStatus.Blank;
	}
	isStatusWithCancelOption() {
		const { InProgress, WaitingApproval } = EProfileBillingStatus;
		const status = this.professionalPerson.status;
		return [InProgress, WaitingApproval].includes(status);
	}

	changeBillingTypeToPersonal() {
		const { Blank, InProgress, RequestingChange } = EProfileBillingStatus;
		const status = this.professionalPerson.status;
		if (this.isStatusWithCancelOption()) {
			return;
		}
		this.professionalPerson.type = this.billingTypes.Natural;
		if (![Blank, InProgress].includes(status)) {
			this.professionalPerson.status = RequestingChange;
		} else {
			this.professionalPerson.status = InProgress;
		}
		this.selectNaturalBillingCard();
	}

	changeBillingTypeToLegalEntity() {
		const { Blank, InProgress, RequestingChange } = EProfileBillingStatus;
		const status = this.professionalPerson.status;

		if (this.isStatusWithCancelOption()) {
			return;
		}
		this.professionalPerson.type = this.billingTypes.Legal;

		if (![Blank, InProgress].includes(status)) {
			this.professionalPerson.status = RequestingChange;
		} else {
			this.professionalPerson.status = InProgress;
		}
	}

	getBillingTypeStartAt(type: EBillingType) {
		if (!this.professionalPerson) {
			return;
		}
		const legalPersonPeriod = this.professionalPerson.lastLegalPersonPeriod;
		const naturalPersonPeriod = this.professionalPerson.lastNaturalPersonPeriod;
		switch (type) {
			case EBillingType.Legal:
				if (legalPersonPeriod?.startsAt) {
					return legalPersonPeriod?.startsAt;
				} else if (naturalPersonPeriod?.endsAt) {
					return moment(naturalPersonPeriod.endsAt).add(1, 'day').toISOString();
				}
				break;
			case EBillingType.Natural:
				if (naturalPersonPeriod?.startsAt) {
					return naturalPersonPeriod?.startsAt;
				} else if (legalPersonPeriod?.endsAt) {
					return moment(legalPersonPeriod.endsAt).add(1, 'day').toISOString();
				}
				break;
		}
	}

	openChangeBillingTypeDialog(personType: EBillingType) {
		const { type, lastNaturalPersonPeriod, status } = this.professionalPerson;
		const { InProgress, WaitingChange, ChangeAccepted } = EProfileBillingStatus;
		const getPeriodStart = () => {
			if (type == EBillingType.Legal) {
				return moment(this.professionalPerson.lastLegalPersonPeriod?.startsAt);
			} else {
				return moment(lastNaturalPersonPeriod?.startsAt);
			}
		};
		if (
			type == personType ||
			this.isStatusWithCancelOption() ||
			[WaitingChange, ChangeAccepted].includes(status)
		) {
			return;
		}
		if (!type || this.professionalPerson.status == InProgress) {
			if (personType == 'natural') {
				this.changeBillingTypeToPersonal();
			} else {
				this.changeBillingTypeToLegalEntity();
			}
			return;
		}
		this.changeBillingTypeDialog = {
			isOpen: true,
			type: personType,
			isLoading: false,
			nextChangeDate: getPeriodStart().add(3, 'months').format('DD/MM/YYYY'),
		};
	}

	resetForm() {
		const formData = this.getCurrentFormData();
		const formAddress = this.getCurrentFormAddress();
		if (this.formIsChanged()) {
			this.resetBankData();
			if (this.professionalPerson.type == this.billingTypes.Legal) {
				formData.patchValue(this.getDefaultLegalFormData());
			} else {
				formAddress
					? formAddress.patchValue(this.getDefaultNaturalFormAddress())
					: null;
				formData.patchValue(this.getDefaultNaturalFormData());
			}
			this.setFormUnchanged();
			this.setBankDataFormUnchanged();
		}
	}

	resetBankData() {
		const paymentAccount =
			this.user.paymentAccount || ({} as IProfessionalPaymentAccount);
		this.bankDataFormParams.patchValue({
			pixType: paymentAccount.pixType,
			pixKey: paymentAccount.pixKey,
			ispb: paymentAccount.ispb,
			dontAcceptPix: paymentAccount.dontAcceptPix,
			bankName: paymentAccount.bankName,
			bankCode: paymentAccount.bankCode,
			bankFullName: paymentAccount.bankFullName,
			agency: paymentAccount.agency,
			account: paymentAccount.account,
			accountDigit: paymentAccount.accountDigit,
		});
	}

	getDefaultLegalFormData() {
		const userData = this.getFormSourceData(this.billingTypes.Legal);
		return {
			fiscalId: userData.fiscalId,
			businessFiscalId: userData.businessFiscalId,
		};
	}

	getDefaultNaturalFormData() {
		const userData = this.getFormSourceData(this.billingTypes.Natural);
		return {
			foreign: userData.naturalPerson?.isForeign,
			fiscalId: userData.fiscalId,
			nationalIdentityCard: userData.naturalPerson?.nationalIdentityCard,
			rne: userData.naturalPerson?.rne,
			pis: userData.naturalPerson?.pis,
			ccm: userData.naturalPerson?.ccm,
			serviceCode: userData.naturalPerson?.serviceCode || [],
		};
	}

	getDefaultNaturalFormAddress() {
		const userData = this.getFormSourceData(this.billingTypes.Natural);
		return {
			postalCode: userData.postalCode,
			address: userData.address,
			addressNumber: userData.addressNumber,
			addressComplement: userData.addressComplement,
			addressCountry: userData.addressCountry,
			neighborhood: userData.neighborhood,
			city: userData.city,
			state: userData.state,
		};
	}

	getDefaultLegalFormAddress() {
		const legalData =
			this.getFormSourceData(this.billingTypes.Legal).legalPerson ||
			({} as IProfessionalLegalPerson);
		return {
			postalCode: legalData.postalCode,
			address: legalData.address,
			addressNumber: legalData.addressNumber,
			addressComplement: legalData.addressComplement,
			neighborhood: legalData.neighborhood,
			city: legalData.city,
			state: legalData.state,
		};
	}
	requestBankDataUpdateConfirmation() {
		return this.bankDataFormParams.dirty && this.hasPaymentAccount;
	}

	saveForm(confirmedUpdate?: boolean) {
		const formData = this.getCurrentFormData();

		formData?.markAllAsTouched();
		this.bankDataFormParams.markAllAsTouched();
		this.showRequiredFiles = true;
		if (!this.allCurrentFormsIsValid() || !this.bankDataFormParams.valid) {
			if (this.bankDataFormParams.valid && !this.professionalPerson.type) {
				const title = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_TYPE_REQUIRED_TITLE'
				);
				const message = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_TYPE_REQUIRED'
				);
				this.notificationService.showError(message, title);
			}
			return;
		}
		if (this.requestBankDataUpdateConfirmation() && !confirmedUpdate) {
			this.showModalUpdateConfirmation('desktop');
			return;
		}
		this.isSaving = true;
		if (this.bankDataFormParams.dirty) {
			this.updatePaymentData(formData.dirty);
		}

		if (formData.dirty || this.documentsChanged) {
			this.updateProfile();
		}
	}

	showModalUpdateConfirmation(type: 'mobile' | 'desktop') {
		this.updateConfirmationDialog = {
			type,
			isOpen: true,
		};
	}

	onConfirmedUpdate() {
		this.updateConfirmationDialog.isOpen = false;
		if (this.updateConfirmationDialog.type === 'mobile') {
			this.saveMobileBankData(true);
		} else {
			this.saveForm(true);
		}
	}

	public allCurrentFormsIsValid() {
		const formData = this.getCurrentFormData();
		return formData?.valid;
	}

	updateLocalData(): void {
		this.userService
			.getProfessionalProfileData(this.user.id)
			.subscribe((user: IProfessionalProfile) => {
				if (user) {
					this.userService.saveData(user);
				}
			});
	}

	getNaturalStatus() {
		if (!this.professionalPerson?.type) {
			return this.billingStatus.Blank;
		} else if (this.professionalPerson?.type == 'natural') {
			return this.professionalPerson.status;
		}
		if (this.professionalPerson.lastNaturalPersonPeriod?.endsAt) {
			return this.billingStatus.Inactive;
		}
		return this.billingStatus.Blank;
	}

	getLegalStatus() {
		if (!this.professionalPerson?.type) {
			return this.billingStatus.Blank;
		} else if (this.professionalPerson?.type == 'legal') {
			return this.professionalPerson.status;
		}
		if (this.professionalPerson.lastLegalPersonPeriod?.endsAt) {
			return this.billingStatus.Inactive;
		}
		return this.billingStatus.Blank;
	}

	getMobilePageName() {
		if (!this.subPageName) {
			return;
		}
		const translatePath = 'MY_ACCOUNT.FINANCIAL.BILLING_CARD.MOBILE_MENU';
		if (this.isBankDataSubpage()) {
			return `MY_ACCOUNT.FINANCIAL.BANK_DATA.MOBILE_MENU.${this.subPageName}`;
		} else if (this.billingTypeSelected == EBillingType.Natural) {
			return `${translatePath}.PERSONAL.${this.subPageName}`;
		} else {
			return `${translatePath}.LEGAL_ENTITY.${this.subPageName}`;
		}
	}

	isBankDataSubpage(): boolean {
		return this.subPageName === 'BANK_DATA';
	}

	getProfessionalPerson(): Promise<IProfessionalPerson> {
		return new Promise(async (resolve) => {
			this.userService.checkProfessionalPerson().subscribe((result) => {
				return resolve(result);
			});
		});
	}

	async setProfessionalPerson() {
		const { ChangeRequested, ChangeAccepted } = EProfileBillingStatus;
		const result = await this.getProfessionalPerson();
		if ([ChangeAccepted].includes(result.status)) {
			const { Natural, Legal } = this.billingTypes;
			result.type = result.type == Natural ? Legal : Natural;
		}
		if ([ChangeRequested].includes(result.status)) {
			const { Natural, Legal } = this.billingTypes;
			result.type = result.type == Natural ? Legal : Natural;
		}
		if (result.status == ChangeAccepted) {
			result.status = EProfileBillingStatus.WaitingChange;
		}

		if ([ChangeRequested].includes(result.status)) {
			result.status = EProfileBillingStatus.WaitingApproval;
		}
		this.professionalPerson = result;
		if (!this.professionalPerson.status) {
			result.status = EProfileBillingStatus.Blank;
		}
		if (this.professionalPerson.type == EBillingType.Natural) {
			this.selectNaturalBillingCard();
		} else {
			this.selectLegalEntityBillingCard();
		}
	}

	onAddedDocument() {
		this.documentsChanged = true;
		this.unsavedChanges.emit(true);
		if (this.isMobileDevice()) {
			this.saveMobileForm();
		}
	}

	public isIdwallCalled(idwallWait: boolean): void {
		this.allowPaymentUpdate = idwallWait;
	}

	public getProfessionalFinancial(professionalResponse): void {
		this.setPaymentMethodUpdateResponse(professionalResponse);
	}
	saveMobileForm() {
		if (this.isBankDataSubpage()) {
			this.saveMobileBankData();
		} else {
			this.saveMobileProfile();
		}
	}

	public saveMobileBankData(confirmedUpdate?: boolean) {
		this.bankDataFormParams.markAllAsTouched();
		if (!this.bankDataFormParams.valid || !this.bankDataFormParams.dirty) {
			return;
		}
		if (this.requestBankDataUpdateConfirmation() && !confirmedUpdate) {
			this.showModalUpdateConfirmation('mobile');
			return;
		}
		this.isSaving = true;
		this.updatePaymentData();
	}

	public saveMobileProfile() {
		const formData = this.getCurrentFormData();

		const formAddress = this.getCurrentFormAddress();
		const formVisible = this.subPageName == 'DATA' ? formData : formAddress;
		formVisible?.markAllAsTouched();

		if (this.subPageName != 'DOCUMENTS' || !formVisible?.dirty) {
			return;
		}

		if (this.allCurrentFormsIsValid() || !this.bankDataFormParams.valid) {
			this.isSaving = true;
			this.updateProfile();
		} else if (this.subPageName != 'DOCUMENTS') {
			this.notificationService.showSuccess(
				this.translateService.instant('MY_ACCOUNT.FINANCIAL.SAVED')
			);
			formVisible.markAsPristine();
		}
	}

	public async updateProfile() {
		this.setFormUnchanged();
		this.updateLocalData();
		this.userUpdated.emit();
		await this.setProfessionalPerson();
		this.isSaving = false;
	}

	private updatePaymentData(skipHideIsSaving?: boolean) {
		const data = this?.bankDataFormParams?.value as IProfessionalPaymentAccount;
		if (data?.dontAcceptPix) {
			data.pixType = null;
			data.pixKey = null;
		}
		this.userService.updatePaymentData(data).subscribe(
			() => {
				this.user.paymentAccount = data;
				this.notificationService.showSuccess(
					this.translateService.instant('MY_ACCOUNT.FINANCIAL.SAVED')
				);
				this.setBankDataFormUnchanged();
				if (!skipHideIsSaving) {
					this.isSaving = false;
				}
			},
			({ error }) => {
				this.onRequestError(error);
			}
		);
	}

	onRequestError(error: ApiError) {
		this.notificationService.showError(
			error?.messageI18n,
			this.translateService.instant('MY_ACCOUNT.FINANCIAL.ERROR_SAVE')
		);
		this.isSaving = false;
	}

	async onRequestCancellation() {
		if (!this.cancellationDialog.isLoading) {
			this.cancellationDialog.isLoading = true;
			switch (this.cancellationDialog.type) {
				case this.cancellationDialogTypes.Cancel:
					this.resetForm();
					break;
				case this.cancellationDialogTypes.RollbackToLegal:
					this.rollbackBillingTypeToLegalEntity();
					break;
				case this.cancellationDialogTypes.RollbackToNatural:
					this.rollbackBillingTypeToPersonal();
					break;
				case this.cancellationDialogTypes.RollbackNaturalToBlank:
				case this.cancellationDialogTypes.RollbackLegalToBlank:
					this.setProfileBillingTypeBlank();
					break;
			}
			this.closeDialogCancellation();
		}
	}

	openDialogCancellation(type: ECancellationDialogTypes) {
		const {
			Cancel,
			RollbackToLegal,
			RollbackToNatural,
			RollbackLegalToBlank,
			RollbackNaturalToBlank,
		} = this.cancellationDialogTypes;
		if (type == Cancel && !this.formIsChanged()) {
			return;
		}
		this.cancellationDialog.type = type;
		if (type === RollbackToNatural && !this.hasNaturalStartsAt()) {
			this.cancellationDialog.type = RollbackLegalToBlank;
		} else if (type === RollbackToLegal && !this.hasLegalStartsAt()) {
			this.cancellationDialog.type = RollbackNaturalToBlank;
		}
		this.cancellationDialog.showDescription = type != Cancel;
		this.cancellationDialog.isOpen = true;
	}

	hasLegalStartsAt(): boolean {
		return !!this.professionalPerson.lastLegalPersonPeriod?.startsAt;
	}

	hasNaturalStartsAt(): boolean {
		return !!this.professionalPerson.lastNaturalPersonPeriod?.startsAt;
	}

	formIsChanged() {
		return (
			this.getCurrentFormData()?.dirty ||
			this.getCurrentFormAddress()?.dirty ||
			this.bankDataFormParams?.dirty ||
			this.documentsChanged
		);
	}

	mobileFormIsDisabled() {
		if (this.isBankDataSubpage()) {
			return !this.bankDataFormParams?.dirty;
		}
		return true;
	}

	initForms() {
		this.forms = <any>{};
		this.initLegalFormData();
		this.initLegalFormAddress();
		this.initNaturalFormData();
		this.initFormAddress();
		Object.keys((formKey) => {
			this.forms[formKey].valueChanges.subscribe(() => {
				this.setUnsavedChanges();
			});
		});
	}

	setUnsavedChanges() {
		if (this.professionalPerson?.type == this.billingTypes.Natural) {
			this.unsavedChanges.emit(
				this.forms.naturalFormData.dirty ||
					this.forms.naturalFormAddress.dirty ||
					this.bankDataFormParams?.dirty
			);
		} else {
			this.unsavedChanges.emit(
				this.forms.legalFormData.dirty || this.bankDataFormParams?.dirty
			);
		}
	}

	onLoadBankForm($event: FormGroup) {
		this.bankDataFormParams = $event;
		this.bankDataFormParams.valueChanges.subscribe(() => {
			this.setUnsavedChanges();
		});
	}

	translatePersonType(personType: 'natural' | 'legal') {
		return this.translateService.instant(
			`MY_ACCOUNT.FINANCIAL.${personType?.toUpperCase()}`
		);
	}

	getCurrentFormAddress() {
		if (this.billingTypeSelected == this.billingTypes.Natural) {
			return this?.forms?.naturalFormAddress;
		}
	}

	public getCurrentFormData() {
		if (this.billingTypeSelected == this.billingTypes.Legal) {
			return this.forms.legalFormData;
		}
		return this.forms.naturalFormData;
	}

	private isNewProfileBilling() {
		return !this.hasLegalStartsAt() && !this.hasNaturalStartsAt();
	}

	private setFormUnchanged() {
		this.unsavedChanges.emit(false);
		this.getCurrentFormData().markAsPristine();
		this.getCurrentFormAddress()?.markAsPristine();
		this.documentsChanged = false;
		this.changeDetector.detectChanges();
	}

	private setBankDataFormUnchanged() {
		this.unsavedChanges.emit(false);
		this.bankDataFormParams?.markAsPristine();
		this.changeDetector.detectChanges();
	}

	private initLegalFormData() {
		const user = this.getFormSourceData(this.billingTypes.Legal);
		this.forms.legalFormData = new FormGroup({
			fiscalId: new FormControl(user.fiscalId || '', {
				validators: [
					Validators.required,
					this.validateControl(this.validateFiscalId),
				],
			}),
			businessFiscalId: new FormControl(user.businessFiscalId, {
				validators: [
					Validators.required,
					this.validateControl(this.validateBusinessFiscalId),
				],
			}),
		});
	}

	getFormSourceData(formType: EBillingType) {
		const { type, professionalData } = this.professionalPerson;
		if (formType == type) {
			return professionalData || ({} as IProfessionalPersonData);
		}
		return this.user;
	}

	private validateMaxLengthArray(array: string[]) {
		return array?.length && array.length <= 3;
	}

	private validatePis(pis) {
		if (this.professionalPerson?.type === this.billingTypes.Natural) {
			return !!pis;
		}
		return true;
	}

	private validateControl(func: CallableFunction) {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!func.bind(this)(control.value)) {
				return { invalid: true };
			}
		};
	}

	private initLegalFormAddress() {
		const user = this.getFormSourceData(this.billingTypes.Legal);
		const legalData = user.legalPerson || ({} as IProfessionalLegalPerson);
		this.forms.legalFormAddress = new FormGroup({
			postalCode: new FormControl(
				legalData.postalCode,
				this.validateControl(this.validatePostalCode)
			),
			address: new FormControl(legalData.address),
			addressNumber: new FormControl(legalData.addressNumber),
			addressComplement: new FormControl(legalData.addressComplement),
			neighborhood: new FormControl(legalData.neighborhood),
			city: new FormControl(legalData.city),
			state: new FormControl(legalData.state),
		});
	}

	private initNaturalFormData() {
		const user = this.getFormSourceData(this.billingTypes.Natural);
		const naturalPerson: IProfessionalNaturalPerson = user.naturalPerson;
		const isForeign = false;
		const form = new FormGroup({
			isForeign: new FormControl(isForeign),
			rne: new FormControl(naturalPerson?.rne),
			fiscalId: new FormControl(user.fiscalId || '', {
				validators: [
					Validators.required,
					this.validateControl(this.validateFiscalId),
				],
			}),
			nationalIdentityCard: new FormControl(
				naturalPerson?.nationalIdentityCard,
				Validators.required
			),
			pis: new FormControl(naturalPerson?.pis, Validators.required),
			ccm: new FormControl(naturalPerson?.ccm),
			serviceCode: new FormControl(naturalPerson?.serviceCode || [], [
				Validators.required,
				this.validateControl(this.validateMaxLengthArray),
			]),
		});
		this.forms.naturalFormData = form;
		this.updateFormDataValidators(isForeign);
		form.get('isForeign').valueChanges.subscribe((isForeignValue) => {
			this.updateFormDataValidators(isForeignValue);
		});
	}

	validateFiscalId(fiscalId: string) {
		return this.cpfService.validate(fiscalId);
	}

	validateBusinessFiscalId(fiscalId: string) {
		return this.cnpjService.validate(fiscalId);
	}

	showBillingCard() {
		return (
			this.professionalPerson?.type === this.billingTypes.Legal &&
			this.professionalPerson.status === this.billingStatus.Active
		);
	}

	hasStripeAccountConnected() {
		return !!this.user.paymentMetadata?.customerId;
	}

	goToStripeCustomerPortal() {
		window.open(
			`https://billing.stripe.com/p/login/fZe3ff5Z59Mwe6Q7ss?prefilled_email=${this.user.email}`
		);
	}

	private updateFormDataValidators(isForeign: boolean) {
		const rne = this.forms.naturalFormData.get('rne');
		const nationalIdentityCard = this.forms.naturalFormData.get(
			'nationalIdentityCard'
		);
		if (isForeign) {
			this.setControlValidators(rne, [Validators.required]);
			nationalIdentityCard.patchValue('');
			this.clearControlValidators(nationalIdentityCard);
		} else {
			this.setControlValidators(nationalIdentityCard, [Validators.required]);
			rne.patchValue('');
			this.clearControlValidators(rne);
		}
	}

	private initFormAddress() {
		const userData = this.getFormSourceData(this.billingTypes.Natural);
		this.forms.naturalFormAddress = new FormGroup({
			liveOutsideBrazil: new FormControl(false),
			postalCode: new FormControl(
				userData.postalCode,
				this.validateControl(this.validatePostalCode)
			),
			address: new FormControl(userData.address),
			addressNumber: new FormControl(userData.addressNumber),
			addressComplement: new FormControl(userData.addressComplement),
			neighborhood: new FormControl(userData.neighborhood),
			city: new FormControl(userData.city),
			state: new FormControl(userData.state),
			addressCountry: new FormControl(
				userData?.addressCountry,
				this.validateControl(this.validateAddressCountry)
			),
		});
	}

	private validateAddressCountry(addressCountry) {
		const liveOutsideBrazil =
			this.forms.naturalFormAddress?.get('liveOutsideBrazil');
		if (
			(!liveOutsideBrazil && this.user.naturalPerson?.liveOutsideBrazil) ||
			liveOutsideBrazil?.value
		) {
			return !!addressCountry;
		}
		return true;
	}

	private validatePostalCode(postalCode: string) {
		return (postalCode || '').replace(/\D/g, '').length === 8;
	}

	private setControlValidators(
		control: AbstractControl,
		validators: ValidatorFn[]
	) {
		control.setValidators(validators);
		control.updateValueAndValidity();
	}

	private clearControlValidators(control: AbstractControl) {
		control.clearValidators();
		control.updateValueAndValidity();
	}

	private closeDialogCancellation() {
		this.cancellationDialog = {
			isOpen: false,
			type: this.cancellationDialogTypes.Cancel,
			showDescription: true,
			isLoading: false,
		};
	}

	private initDialogStatus() {
		this.changeBillingTypeDialog = {
			isOpen: false,
			isLoading: false,
			type: EBillingType.Natural,
		};
		this.updateConfirmationDialog = {
			isOpen: false,
			type: this.isMobileDevice() ? 'mobile' : 'desktop',
		};
		this.cancellationDialog = {
			isOpen: false,
			type: this.cancellationDialogTypes.Cancel,
			showDescription: true,
		};
	}

	private async getUser() {
		this.user = await this.userService.getProfileData();
		if (typeof this.user === 'string') {
			this.user = {} as IProfessionalProfile;
		}
		const paymentAccount =
			this.user.paymentAccount || ({} as IProfessionalPaymentAccount);
		this.hasPaymentAccount =
			!!paymentAccount.account && !!paymentAccount.agency;
		this.setRequiredProfileFields();
	}
}
